import { useEffect, useState } from 'react';
import axios from 'axios';
import { getGameType } from '../../../variables/gameTypes';

// Read-only "what did the student actually do in this game" renderer for
// embed_grid - the same spirit as AnsweredInteractivePreview.jsx's other
// components (frozen, colored correct/wrong, fed from the real saved
// resultJson instead of local play state), just kept in its own file since
// it needs its own cards fetch (GridCard[] isn't embedded on the block the
// way config is for every other type - same fetch EmbedGridPreview.jsx,
// the admin authoring-preview version of this, already does).
//
// resultJson shapes below are verified against english-app's own 4 game
// components (gridGames.tsx - the actual code that writes these rows), not
// guessed:
//   type 1 "Числова сітка": {flippedIds:[cardId,...]}  - which cards the
//     student has revealed at least once (grows only, never shrinks - a
//     card flipped back to front client-side doesn't un-save)
//   type 2 "Квіз":          {answers:{cardId:{typed, checked}}}
//   type 3 "Слова":         {cards:{cardId:[letter|null, ...]}} - exact
//     per-slot letters, index-aligned to the word's own characters
//   type 4 "Підбір пар":    {placedWords:{sentenceCardId: word}} - the
//     platform only ever persists a CORRECT drop (a wrong one is a
//     transient 400ms flash, never saved), so every entry here is correct
//     by construction - nothing to grade, just to show as filled.

function safeParseResult(resultJson, fallback) {
   if (!resultJson) return fallback;
   try {
      const parsed = JSON.parse(resultJson);
      return parsed && typeof parsed === 'object' ? parsed : fallback;
   } catch (e) {
      return fallback;
   }
}

const GAP_MARKER_RE = /_+/;

function AnsweredFlipGrid({ cards, resultJson }) {
   const result = safeParseResult(resultJson, { flippedIds: [] });
   const flippedIds = (result.flippedIds || []).map(Number);
   if (flippedIds.length === 0) {
      return <p className='workspace-activity-empty-hint'>Студент ще не відкрив жодної картки.</p>;
   }
   return (
      <div className='course-preview-flip-grid'>
         {cards.map((c, i) => (
            <div className='course-preview-flip-card' key={c.id}>
               <div className={'course-preview-flip-card-inner' + (flippedIds.includes(c.id) ? ' flipped' : '')}>
                  <div className='course-preview-flip-card-front'>{i}</div>
                  <div className='course-preview-flip-card-back'>{c.value}</div>
               </div>
            </div>
         ))}
      </div>
   );
}

function AnsweredQuizGrid({ cards, resultJson }) {
   const result = safeParseResult(resultJson, { answers: {} });
   const answers = result.answers || {};
   const anyAnswered = cards.some(c => answers[c.id] && answers[c.id].typed);
   if (!anyAnswered) {
      return <p className='workspace-activity-empty-hint'>Студент ще не відповів на жодне питання.</p>;
   }
   return (
      <div className='course-preview-quiz-gap-list'>
         {cards.map(c => {
            const question = c.answer || '';
            const correctWord = (c.value || '').trim();
            const hasAnswer = correctWord.length > 0;
            const gapMatch = question.match(GAP_MARKER_RE);
            const entry = answers[c.id] || { typed: '', checked: false };
            const isCorrect = entry.checked && entry.typed.trim().toLowerCase() === correctWord.toLowerCase();
            const stateClass = entry.checked ? (isCorrect ? ' correct' : ' wrong') : '';
            const display = entry.typed || '—';
            return (
               <div className='course-preview-quiz-gap-card' key={c.id}>
                  <p className='course-preview-question-text'>
                     {gapMatch ? (
                        <>
                           {question.slice(0, gapMatch.index)}
                           <span className={'course-preview-blank-input course-preview-quiz-gap-input' + stateClass}>{display}</span>
                           {question.slice(gapMatch.index + gapMatch[0].length)}
                        </>
                     ) : question}
                  </p>
                  {!gapMatch ? (
                     <span className={'course-preview-blank-input course-preview-quiz-gap-input' + stateClass}>{display}</span>
                  ) : ''}
                  {hasAnswer && entry.checked ? (
                     isCorrect
                        ? <span className='course-preview-quiz-gap-feedback correct'>Правильно!</span>
                        : <span className='course-preview-quiz-gap-feedback wrong'>Правильна відповідь: {correctWord}</span>
                  ) : ''}
               </div>
            );
         })}
      </div>
   );
}

function AnsweredWordBuilder({ cards, resultJson }) {
   const result = safeParseResult(resultJson, { cards: {} });
   const saved = result.cards || {};
   const anyAnswered = cards.some(c => saved[c.id] && saved[c.id].some(Boolean));
   if (!anyAnswered) {
      return <p className='workspace-activity-empty-hint'>Студент ще не почав складати жодного слова.</p>;
   }
   return (
      <div className='course-preview-word-list'>
         {cards.map(c => {
            const correctLetters = (c.value || '').split('');
            const slots = saved[c.id] || Array(correctLetters.length).fill(null);
            return (
               <div className='course-preview-word-card' key={c.id}>
                  <div className='course-preview-word-slots'>
                     {slots.map((ch, i) => {
                        const state = ch ? (ch.toLowerCase() === (correctLetters[i] || '').toLowerCase() ? ' correct' : ' wrong') : '';
                        return <div key={i} className={'course-preview-word-slot' + state}>{ch ? ch.toUpperCase() : ''}</div>;
                     })}
                  </div>
               </div>
            );
         })}
      </div>
   );
}

function AnsweredGapFill({ cards, resultJson }) {
   const result = safeParseResult(resultJson, { placedWords: {} });
   const placedWords = result.placedWords || {};
   if (Object.keys(placedWords).length === 0) {
      return <p className='workspace-activity-empty-hint'>Студент ще не підібрав жодної пари.</p>;
   }
   const sorted = [...cards].sort((a, b) => a.id - b.id);
   const hasBankCard = sorted.length > 0 && !sorted[0].value;
   const sentenceCards = hasBankCard ? sorted.slice(1) : sorted;
   return (
      <div className='course-preview-gapfill-sentences'>
         {sentenceCards.map((c, i) => {
            const filled = placedWords[c.id];
            const gapMatch = (c.value || '').match(GAP_MARKER_RE);
            let sentence = c.value;
            if (filled && gapMatch) {
               const parts = c.value.split(gapMatch[0]);
               sentence = <>{parts[0]}<strong className='course-preview-gap-filled'>{filled}</strong>{parts[1]}</>;
            }
            return (
               <div className={'course-preview-gapfill-sentence' + (filled ? ' filled' : '')} key={c.id}>
                  <span className='course-preview-gapfill-index'>{i + 1}.</span>
                  <span className='course-preview-gapfill-text'>{sentence}</span>
                  {filled ? <span className='course-preview-gapfill-check'>✓</span> : ''}
               </div>
            );
         })}
      </div>
   );
}

const ANSWERED_GRID_TYPES = {
   1: AnsweredFlipGrid,
   2: AnsweredQuizGrid,
   3: AnsweredWordBuilder,
   4: AnsweredGapFill,
};

// block.grid arrives embedded on the block (same as block.conversation for
// embed_conversation) - only the cards themselves need their own fetch,
// same call EmbedGridPreview.jsx/GameCardsEditor.jsx already use.
export default function AnsweredEmbedGrid({ block, resultJson }) {
   const grid = block.grid;
   const gridId = grid ? grid.id : null;
   const [cards, setCards] = useState(null);

   useEffect(() => {
      if (!gridId) {
         setCards([]);
         return;
      }
      axios.get('/api/gridcard?grid=' + gridId, { withCredentials: true })
         .then(response => setCards(Array.isArray(response.data) ? response.data : []))
         .catch(() => setCards([]));
   }, [gridId]);

   if (!grid) return <span className='exercise-block-empty-hint'>Гру не обрано</span>;
   if (cards === null) return <span className='exercise-block-empty-hint'>Завантаження...</span>;

   const gameType = getGameType(grid.type);
   const AnsweredType = ANSWERED_GRID_TYPES[grid.type] || AnsweredFlipGrid;

   return (
      <div className='course-preview-embed-grid'>
         <div className='course-preview-embed-grid-header'>
            <span className='course-preview-embed-grid-badge' style={{ '--accent': gameType.accent }}>{gameType.label}</span>
         </div>
         {cards.length === 0 ? (
            <span className='exercise-block-empty-hint'>Гра порожня</span>
         ) : (
            <AnsweredType cards={cards} resultJson={resultJson} />
         )}
      </div>
   );
}
